import React from 'react';
import Skeleton from 'react-loading-skeleton';
import { TabView, TabPanel } from 'primereact/tabview';
import { Button } from 'primereact/button';
import RequestsList from './components/RequestsList';

type RequestsPanelProps = {
  isPendingLoading: boolean;
  pendingError: any;
  pendingRequests: any[];
  isCompletedLoading: boolean;
  completedError: any;
  completedRequests: any[];
  approveRequest: (id: any) => void;
  declineRequest: (id: any) => void;
};

const RequestsPanel: React.FC<RequestsPanelProps> = ({
  isPendingLoading,
  pendingError,
  pendingRequests,
  isCompletedLoading,
  completedError,
  completedRequests,
  approveRequest,
  declineRequest,
}) => {
  const [tabIndex, setTabIndex] = React.useState(0);
  // Custom tab button headers with icons
  const tabButtons = [
    {
      label: 'Pending',
      icon: 'pi pi-clock',
      index: 0,
    },
    {
      label: 'Completed',
      icon: 'pi pi-check-circle',
      index: 1,
    },
  ];

  return (
    <div style={{ display: 'flex', flexDirection: 'column', height: '100%', width: '100%', minHeight: 0, overflow: 'hidden' }}>
      <div style={{ display: 'flex', gap: 6, justifyContent: 'flex-end', margin: '8px 12px 8px 0', flexShrink: 0 }}>
        {tabButtons.map(btn => {
          const isPending = btn.index === 0;
          const isCompleted = btn.index === 1;
          const isActive = tabIndex === btn.index;
          const color = isPending ? '#bfa100' : '#1b8a3a'; // yellow for pending, green for completed
          const bg = isActive ? (isPending ? '#fffbe6' : '#e8f5e9') : '#fff';
          const border = isActive ? `2px solid ${color}` : '1px solid #cfd8dc';
          return (
            <Button
              key={btn.index}
              label={btn.label}
              icon={btn.icon}
              className={isActive ? 'p-button p-button-raised p-button-text' : 'p-button p-button-outlined p-button-text'}
              style={{
                background: bg,
                color: color,
                border: border,
                fontWeight: 600,
                boxShadow: 'none',
                padding: '0.1rem 0.7rem',
                borderRadius: 6,
                fontSize: 10,
                minWidth: 60,
                transition: 'all 0.2s',
              }}
              onClick={() => setTabIndex(btn.index)}
            />
          );
        })}
      </div>
      <div style={{ flex: 1, minHeight: 0, overflow: 'hidden', display: 'flex', flexDirection: 'column' }}>
        {tabIndex === 0 ? (
          isPendingLoading ? (
            <div style={{ padding: '12px', flex: 1, overflowY: 'auto' }}>
              {Array.from({ length: 4 }).map((_, i) => (
                <div key={i} className="requestRow">
                  <div className="requestRow__left" style={{ flex: 1 }}>
                    <Skeleton width="40%" height="1rem" style={{ marginBottom: 8 }} />
                    <Skeleton width="35%" height="0.85rem" style={{ marginBottom: 6 }} />
                    <div style={{ marginTop: 6, marginBottom: 0, display: 'flex', gap: 8, alignItems: 'center' }}>
                      <Skeleton width="80px" height="18px" borderRadius="6px" />
                      <Skeleton width="100px" height="0.85rem" />
                    </div>
                  </div>
                  <div className="requestRow__right" style={{ display: 'flex', gap: 6, flexShrink: 0 }}>
                    <Skeleton width="70px" height="28px" borderRadius="6px" />
                    <Skeleton width="140px" height="28px" borderRadius="6px" />
                  </div>
                </div>
              ))}
            </div>
          ) : pendingError ? (
            <div className="page__muted" style={{ padding: '12px' }}>{pendingError}</div>
          ) : pendingRequests.length === 0 ? (
            <div className="panel__emptyState">
              <div className="panel__emptyState__iconWrap">
                <i className="pi pi-inbox panel__emptyState__icon" />
              </div>
              <div className="panel__emptyState__title">No pending requests</div>
              <div className="panel__emptyState__sub">All caught up — no requests are waiting for action.</div>
            </div>
          ) : (
            <RequestsList
              items={pendingRequests}
              onApprove={approveRequest}
              onDecline={declineRequest}
              pageSize={5}
            />
          )
        ) : (
          isCompletedLoading ? (
            <div style={{ padding: '12px', flex: 1, overflowY: 'auto' }}>
              {Array.from({ length: 4 }).map((_, i) => (
                <div key={i} className="requestRow">
                  <div className="requestRow__left" style={{ flex: 1 }}>
                    <Skeleton width="40%" height="1rem" style={{ marginBottom: 8 }} />
                    <Skeleton width="35%" height="0.85rem" style={{ marginBottom: 6 }} />
                    <div style={{ marginTop: 6, marginBottom: 0, display: 'flex', gap: 8, alignItems: 'center' }}>
                      <Skeleton width="80px" height="18px" borderRadius="6px" />
                      <Skeleton width="100px" height="0.85rem" />
                    </div>
                  </div>
                  <div className="requestRow__right" style={{ display: 'flex', gap: 6, flexShrink: 0 }}>
                    <Skeleton width="70px" height="28px" borderRadius="6px" />
                    <Skeleton width="140px" height="28px" borderRadius="6px" />
                  </div>
                </div>
              ))}
            </div>
          ) : completedError ? (
            <div className="page__muted" style={{ padding: '12px' }}>{completedError}</div>
          ) : completedRequests.length === 0 ? (
            <div className="panel__emptyState">
              <div className="panel__emptyState__iconWrap">
                <i className="pi pi-check-circle panel__emptyState__icon" />
              </div>
              <div className="panel__emptyState__title">No completed requests</div>
              <div className="panel__emptyState__sub">Completed requests will appear here once processed.</div>
            </div>
          ) : (
            <RequestsList
              items={completedRequests}
              pageSize={5}
            />
          )
        )}
      </div>
    </div>
  );
};

export default RequestsPanel;